import { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import styled from 'styled-components';

import { Loading, Text } from 'shell/components';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const NoFileSelectedPlaceHolder = styled.div`
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const DocumentWrapper = styled.div`
    border: 1px solid #d3d2d7;
    margin: 16px;
    width: calc(100% - 32px);
    height: 80vw;
    background: #e2e1e5;
    overflow: auto;

    ${({ isPdf }) =>
        isPdf &&
        `
        &:focus-within {
            border-radius: 4px;
            outline: 3px solid #bfcdf4;
        }
    `}

    & canvas {
        margin: auto;
    }

    & > .react-pdf__Document:focus {
        outline: none;
    }
`;

const StyledPage = styled(Page)`
    margin-bottom: 15px;

    .annotationLayer {
        display: none;
    }

    & canvas {
        border: 1px solid #9e9e9e;
    }
`;

const StyledImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const FileViewer = ({ device, docUrl = '', fileType = '' }) => {
    const [numPages, setNumPages] = useState(null);
    const [divWidth, setDivWidth] = useState(0);
    const docWrapper = useRef();

    const displayFile = useMemo(() => {
        if (docUrl) {
            return {
                url: docUrl,
                fileType: fileType,
            };
        }
    }, [docUrl, fileType]);

    useEffect(() => {
        const currentDocWrapper = docWrapper.current;

        setDivWidth(docWrapper?.current?.clientWidth);

        window.addEventListener('resize', () => {
            setDivWidth(docWrapper?.current?.clientWidth);
        });

        return () => {
            window.removeEventListener('resize', () => {
                setDivWidth(currentDocWrapper.clientWidth);
            });
        };
    }, []);

    useEffect(() => {
        const elements = document.getElementsByClassName('react-pdf__Document');
        if (elements?.length) {
            elements[0].setAttribute('tabindex', 0);
        }
    }, []);

    switch (fileType) {
        case 'application/pdf':
            return (
                <DocumentWrapper ref={docWrapper} device={device} isPdf>
                    <Document
                        file={displayFile?.url}
                        options={{ workerSrc: '/pdf.worker.js' }}
                        loading={<Loading sx={{ marginTop: '10vh' }} />}
                        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    >
                        {Array.from(new Array(numPages), (el, index) => (
                            <StyledPage
                                key={`page_${index + 1}`}
                                pageNumber={index + 1}
                                width={divWidth - (device === 'mobile' ? 20 : 70)}
                                loading={``}
                            />
                        ))}
                    </Document>
                </DocumentWrapper>
            );
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return (
                <iframe
                    title={'file'}
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docUrl)}`}
                    width={'100%'}
                    height={'100%'}
                />
            );
        case 'image/png':
        case 'image/jpg':
        case 'image/jpeg':
            return (
                <DocumentWrapper ref={docWrapper} device={device}>
                    <StyledImg src={displayFile.url} alt='Image Preview' />
                </DocumentWrapper>
            );
        default:
            return (
                <DocumentWrapper ref={docWrapper} device={device}>
                    <NoFileSelectedPlaceHolder>
                        <Text variant={'p2'}>No Viewable File</Text>
                    </NoFileSelectedPlaceHolder>
                </DocumentWrapper>
            );
    }
};

export default FileViewer;
